import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useLanguage } from '@/contexts/LanguageContext';
import PrimaryButton from '@/components/PrimaryButton';
import StageSheetShell, { StagePlaceholderIllustration } from '@/components/StageSheetShell';

export interface PendingTransition {
  fromStageNumber: number;
  fromStageKey: string;
  toStageNumber: number;
  toStageKey: string;
  enteredAt: string | null;
}

export const STAGE_TRANSITION_ACK_PREFIX = 'STAGE_TRANSITION_ACKED';

export const ackKeyFor = (userId: string, t: PendingTransition): string =>
  `${STAGE_TRANSITION_ACK_PREFIX}:${userId}:${t.toStageKey}:${t.enteredAt ?? 'unknown'}`;

interface Props {
  visible: boolean;
  pendingTransition: PendingTransition | null;
  userId: string | null;
  onDismiss: () => void;
}

export default function StageTransitionSheet({
  visible,
  pendingTransition,
  userId,
  onDismiss,
}: Props) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (visible) setBusy(false);
  }, [visible]);

  if (!pendingTransition || !userId) return null;

  const toNum = pendingTransition.toStageNumber;
  const fromNum = pendingTransition.fromStageNumber;

  const stageName = t(`recoveryStages.${toNum}.name`) as string;
  const stageDescription = t(`recoveryStages.${toNum}.description`) as string;
  const fromName = t(`recoveryStages.${fromNum}.name`) as string;

  const headerTitle = (t('stageTransition.eyebrow') as string) || 'NOVA FASE';
  const transitionLabel = ((t('stageTransition.transitionLabel') as string) || '')
    .replace('{from}', fromName)
    .replace('{to}', stageName);

  const ack = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await AsyncStorage.setItem(ackKeyFor(userId, pendingTransition), new Date().toISOString());
    } catch {
      // non-blocking — worst case the sheet shows again next cold start
    }
    onDismiss();
  };

  return (
    <StageSheetShell
      visible={visible}
      title={headerTitle}
      onClose={ack}
      footer={
        <PrimaryButton
          title={t('stageTransition.primaryCta') as string}
          onPress={ack}
          disabled={busy}
        />
      }
    >
      <View style={styles.illustrationWrap}>
        <StagePlaceholderIllustration />
      </View>
      <Text style={styles.transitionLabel}>{transitionLabel}</Text>
      <Text style={styles.stageName}>{stageName}</Text>
      <Text style={styles.body}>{stageDescription}</Text>
    </StageSheetShell>
  );
}

const styles = StyleSheet.create({
  illustrationWrap: {
    width: 260,
    height: 260,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  transitionLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    letterSpacing: 0.6,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 6,
    opacity: 0.9,
  },
  stageName: {
    fontFamily: 'Sentient-Regular',
    fontSize: 24,
    lineHeight: 28.8,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
    maxWidth: 345,
  },
  body: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#FFFFFF',
    textAlign: 'center',
    maxWidth: 345,
  },
});
